/**
 * MER-512: resolve a widget's effective equipment_type filter under an
 * active page-level LOB (services) selection.
 *
 * The page-level LOB chip used to inject `equipment_type` ONLY when the
 * widget declared none (MER-163 "never overwrite an existing filter at the
 * same key"). But the template declares a multi-value rollup list
 * `{op: in, value: [...all enabled LOBs...]}` on most KPI Summary tiles
 * (resolved per-tenant by MER-422's applyEnabledServicesToLobFilter). The
 * never-overwrite rule froze those tiles: selecting an LOB was a VISIBLE
 * no-op (AJ 2026-06-12: BD Disposal Cost Paid stayed $175,508 when the user
 * picked Roll Off). This narrows the declared rollup list to the selection
 * instead, so the chip works, while leaving a single pinned LOB (per-LOB P&L
 * card, e.g. `equipment_type: roll_off`) untouched.
 *
 * Clause-shape contract: an `{op: in, value: [...]}` clause is ALWAYS
 * narrowable, regardless of length, and NEVER widened: the result is its
 * intersection with the selection, or the declared list unchanged. That covers
 * both shapes resolve-manifest.mjs emits: the per-tenant all-LOB rollup
 * (MER-422) and a narrower scope it keeps as authored (MER-694, e.g. the
 * roll_off-only $/ton tiles, which resolve to [roll_off] under any selection).
 * The resolver never emits the degenerate empty list handled below. A pin that
 * ignores the chip entirely is a scalar string (`equipment_type: roll_off`) or
 * `{op: eq, value: ...}`; those return null (left unchanged).
 *
 * Pure (no React, no manifest import) so the node:test suite imports it
 * directly. globalFilters.ts is the only caller and owns the
 * equipment-aware-source guard (MER-423): this helper is reached only for
 * sources that actually carry the column.
 *
 * MER-629: a tile can be scoped to one line of business by something that is
 * NOT an equipment_type filter -- strict per-source work_type literals ("FL
 * Delivery"), a KPI Summary column computed only for one line (rev_per_lift),
 * or a customer-specific work-type list from config. Such a tile declares that
 * scope in `lob_scope` and passes it here as `impliedScope`. Injecting the
 * chip on top of it adds a SECOND, conflicting LOB filter: selecting Roll Off
 * emptied Net FL Containers outright. An implied scope therefore reads exactly
 * like a scalar pin -- the chip never touches the tile, `lobScopeNote` labels
 * its face -- which is already how its neighbour FL Deliveries behaves. It is
 * deliberately NOT turned into an equipment_type filter: on Best Disposal one
 * FL Delivery row carries equipment_type 'asl' and the swap numerator spans
 * hook_lift and tractor_trailer, so pinning would move live numbers.
 *
 * @param {unknown} declared  widget.filter.equipment_type, or undefined
 * @param {ReadonlyArray<string>} selected  the active services selection
 * @param {unknown} [impliedScope]  widget.lob_scope when it is a list of
 *   service ids: the lines the tile's population already covers.
 * @returns {{ op: "in", value: string[] } | null}
 *   A NEW equipment_type clause to set, or null to leave the widget's
 *   declared filter unchanged.
 */
export function resolveLobFilter(declared, selected, impliedScope) {
  if (!Array.isArray(selected) || selected.length === 0) return null;

  // No declared pin: inject the selection directly. This is the original
  // MER-163 behavior (the chip drives an otherwise-unfiltered tile) -- unless
  // the tile declares an implied scope (MER-629), in which case it is already
  // filtered to its line by other means and a second filter would conflict.
  if (declared === undefined || declared === null) {
    if (isImpliedScope(impliedScope)) return null;
    return { op: "in", value: [...selected] };
  }

  // Multi-value rollup list: narrow it to the selection (intersection,
  // declared order preserved). This is the MER-512 fix. The common rollup
  // tile declares the full enabled set, so the intersection equals the
  // selection; a partial list (a 2-LOB rollup) narrows to its overlap.
  if (typeof declared === "object" && declared.op === "in" && Array.isArray(declared.value)) {
    // Degenerate empty declared list (a resolve-manifest edge that nothing
    // structurally forbids): treat as unpinned and inject the selection, so
    // the chip still works instead of narrowing to a permanent empty `in`.
    if (declared.value.length === 0) return { op: "in", value: [...selected] };
    const sel = new Set(selected);
    const narrowed = declared.value.filter((v) => sel.has(v));
    // Empty intersection => the selection sits outside this tile's declared
    // coverage. Keep the declared list rather than emit an empty `in` that
    // matches no rows (which would render n/a and read as broken).
    if (narrowed.length === 0) return null;
    return { op: "in", value: narrowed };
  }

  // Scalar string pin (per-LOB P&L card) or any other clause shape (op:eq, or
  // an op:in with a NON-array value): intentionally pinned. Never override.
  return null;
}

/**
 * MER-694: the face note for a tile whose declared equipment_type in-list
 * does not cover every service line the page is showing.
 *
 * resolveLobFilter only ever narrows or keeps a declared list. An all-line
 * rollup resolves to the customer's enabled_services, which is also what the
 * chip offers, so it always covers the lines in view. A scope does not: the
 * roll_off-only $/ton tiles keep showing Roll Off when the selection leaves it
 * out, and show only their overlap when the selection is wider. With nothing
 * on the face that reads as the chip working on the neighbouring tiles and
 * silently not on this one, the bug class MER-512 closed with "Not filtered by
 * LOB".
 *
 * The lines in view are the selection, or every enabled line when nothing is
 * selected (the default page load). Without the default case the Overview row
 * showed an unlabeled Roll-Off-only Avg Cost / Ton beside all-line Total Tons
 * and Total Disposal Cost. A selection is first cut to the enabled lines (the
 * chip offers no others), so a stale link naming a line the customer no
 * longer runs reads as the default view instead of noting every all-line tile.
 *
 * Pure, and label-agnostic so node:test can drive it; globalFilters.ts passes
 * the shared humanize helper and the manifest's enabled_services.
 *
 * MER-629: a tile with an implied scope (`lob_scope` as a list, see
 * resolveLobFilter) shows only those lines whatever the chip says, so it needs
 * the same note for the same reason. Without it, Net FL Containers under the
 * Roll Off chip would silently keep showing front-load and read as a chip that
 * did nothing.
 *
 * @param {unknown} declared  widget.filter.equipment_type, or undefined
 * @param {ReadonlyArray<string>} selected  the active services selection
 * @param {(lob: string) => string} labelFor  display label for one LOB id
 * @param {ReadonlyArray<string>} [enabled]  manifest enabled_services. When
 *   omitted the selection alone is the view and an empty selection gets no
 *   note.
 * @param {unknown} [impliedScope]  widget.lob_scope when it is a list.
 * MER-616: a SCALAR (or op:eq) pin now gets the note too, but only while a
 * selection is active. Chris, 2026-09-02, on the roll-off-pinned disposal
 * tile: "I see it go to roll off ... it stayed identical. I would expect this
 * to be a little different." It stayed identical because it had always been
 * roll off, and nothing on the tile said so. The assumption this code used to
 * make -- that a scalar pin is a per-LOB card whose title names its line -- is
 * true of "Front Load - P&L" and false of "Work Orders With a Disposal
 * Ticket", and a reader cannot tell which kind they are looking at.
 *
 * Deliberately narrower than the in-list rule: no note on a page with nothing
 * selected. 50 of the 185 DWS widgets carry a scalar pin, almost all of them
 * sitting under a section header that already names the line ("FEL
 * Productivity"), so noting every one on the default load would add 50
 * permanent chips to answer a question nobody asked yet. The question is asked
 * the moment someone touches the chip, and that is when the note appears.
 *
 * @returns {string | null}
 *   "<labels> only" naming the lines the tile actually shows, when that set
 *   leaves out any line in view; null otherwise. Unpinned tiles take the
 *   selection directly and the degenerate empty list injects it, so neither
 *   gets a note.
 */
export function lobScopeNote(declared, selected, labelFor, enabled, impliedScope) {
  const view = linesInView(selected, enabled);
  if (view.length === 0) return null;
  // An implied scope only speaks for a tile that declares no equipment_type of
  // its own; a declared clause is the stronger statement and wins.
  if ((declared === undefined || declared === null) && isImpliedScope(impliedScope)) {
    if (view.every((lob) => impliedScope.includes(lob))) return null;
    return `${impliedScope.map((lob) => labelFor(lob)).join(", ")} only`;
  }
  // MER-616: a single pinned line, which the chip can never move.
  const pinned = scalarLobPin(declared);
  if (pinned !== null) {
    if (!selectionIsActive(selected, enabled)) return null;
    if (view.length === 1 && view[0] === pinned) return null;
    return `${labelFor(pinned)} only`;
  }
  if (!declared || typeof declared !== "object" || declared.op !== "in") return null;
  if (!Array.isArray(declared.value) || declared.value.length === 0) return null;
  const resolved = resolveLobFilter(declared, Array.isArray(selected) ? selected : []);
  const shown = resolved === null ? declared.value : resolved.value;
  if (view.every((lob) => shown.includes(lob))) return null;
  return `${shown.map((lob) => labelFor(lob)).join(", ")} only`;
}

/**
 * MER-629: true when `lob_scope` carries a list of service ids, the shape that
 * says "this tile's population is already restricted to these lines". The
 * other two `lob_scope` values are the resolver's mode strings
 * ("all_enabled" / "fixed", MER-739) and say nothing about the chip.
 */
export function isImpliedScope(value) {
  return Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string");
}

/**
 * MER-616: the one line a scalar string or `{op: "eq"}` clause pins the tile
 * to, or null for every other clause shape. These are exactly the shapes
 * resolveLobFilter refuses to touch, which is why they need a note: the chip
 * moves every neighbouring tile and cannot move this one.
 */
function scalarLobPin(declared) {
  if (typeof declared === "string") {
    const pin = declared.trim();
    return pin.length > 0 ? pin : null;
  }
  if (declared && typeof declared === "object" && declared.op === "eq") {
    return typeof declared.value === "string" && declared.value.trim().length > 0
      ? declared.value.trim()
      : null;
  }
  return null;
}

/**
 * MER-616: true when the reader has actually selected something the chip
 * offers, as opposed to the default page where `linesInView` falls back to
 * every enabled line. A stale link naming a line the customer no longer runs
 * reads as the default view here for the same reason it does there.
 */
function selectionIsActive(selected, enabled) {
  const sel = Array.isArray(selected) ? selected : [];
  if (sel.length === 0) return false;
  if (!Array.isArray(enabled) || enabled.length === 0) return true;
  return sel.some((lob) => enabled.includes(lob));
}

/** The service lines the page is showing: see lobScopeNote. */
function linesInView(selected, enabled) {
  const sel = Array.isArray(selected) ? selected : [];
  if (!Array.isArray(enabled) || enabled.length === 0) return sel;
  const offered = sel.filter((lob) => enabled.includes(lob));
  return offered.length > 0 ? offered : enabled;
}
